import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Coins } from "lucide-react";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FilePicker } from "@/components/upload/FilePicker";
import { startBatch } from "@/lib/useBatch";

export { DRAWING_ERR, DWG_ERR } from "@/components/upload/FilePicker";
export const OUT_OF_CREDITS = "You're out of credits. Buy more to read drawings.";

export function OutOfCredits() {
  const owner = useAuth().user?.role === "owner";
  return (
    <div data-testid="out-of-credits" className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/5 px-8 py-14 text-center">
      <div className="mb-6 grid h-16 w-16 place-items-center rounded-2xl border border-red-500/30 bg-slate-900"><Coins className="h-7 w-7 text-red-300" /></div>
      <p data-testid="out-of-credits-message" className="font-display text-xl font-bold text-slate-100">{owner ? OUT_OF_CREDITS : "Your company is out of credits."}</p>
      <p className="mt-2 text-sm text-slate-400">Each upload of up to 5 files uses 1 credit for schedules, or the plans-only price when no schedule is found. Editing, saving and PDFs stay free.</p>
      {!owner && <p data-testid="ask-owner-message" className="mt-4 text-sm font-medium text-slate-200">Ask your company owner to buy more credits.</p>}
      {owner && <Link to="/billing" data-testid="buy-credits-button"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400">
        <Coins className="h-4 w-4" /> Buy credits
      </Link>}
    </div>
  );
}

export function UploadDropzone() {
  const navigate = useNavigate();
  const { company, setCredits } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const start = async (files) => {
    setError("");
    setBusy(true);
    try {
      const data = await startBatch(files);
      setCredits(data.balance);
      navigate(`/quotes/new?batch=${data.batch_id}`);
    } catch (e) {
      if (e.response?.status === 402) setCredits(0);
      else setError(errorMessage(e));
      setBusy(false);
    }
  };
  if (company && (company.credits ?? 0) <= 0) return <OutOfCredits />;
  return (
    <div>
      <FilePicker busy={busy} onStart={start} title="Drop window/door schedules and drawings" />
      {error && <p data-testid="upload-start-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
    </div>
  );
}
